import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSocket } from "../../utils/socket";
import { motion } from "framer-motion";

// Hàm tiện ích để ánh xạ trạng thái với màu sắc
const getStatusStyles = (enabled) => {
  return enabled
    ? { backgroundColor: "bg-green-500", textColor: "text-white" } // Nền xanh lá, chữ trắng
    : { backgroundColor: "bg-red-500", textColor: "text-white" }; // Nền đỏ, chữ trắng
};

const AdminWithdrawalPaymentGateways = () => {
  const { fetchWithRetry, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [gateways, setGateways] = useState([]);
  const [filteredGateways, setFilteredGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [newGateway, setNewGateway] = useState({ name: "", label: "", description: "" });

  // State cho tìm kiếm, lọc, sắp xếp, phân trang
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortField, setSortField] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Tải danh sách cổng thanh toán
  const loadPaymentGateways = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetchWithRetry({ method: "get", url: "/api/admin/coins/all-payment-gateways" });

      // Log dữ liệu trả về để kiểm tra
      console.log("Payment Gateways Data:", res.data);

      const gatewaysData = res.data || [];
      setGateways(gatewaysData);
      setFilteredGateways(gatewaysData);

      if (gatewaysData.length === 0) {
        setError("Không có cổng thanh toán nào được tìm thấy.");
      }
    } catch (error) {
      console.error("Lỗi khi tải danh sách cổng thanh toán:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi tải danh sách cổng thanh toán. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError("Không thể tải danh sách cổng thanh toán!");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Xử lý thêm cổng thanh toán
  const handleAddPaymentGateway = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/coins/add-payment-gateway",
        data: newGateway,
      });
      setNotificationMessage(response.data.message || "Thêm cổng thanh toán thành công!");
      setNewGateway({ name: "", label: "", description: "" });
      loadPaymentGateways();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi thêm cổng thanh toán:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi thêm cổng thanh toán. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể thêm cổng thanh toán!");
      }
    }
  };

  // Xử lý cập nhật cổng thanh toán
  const handleUpdatePaymentGateway = async (gatewayId, updatedData) => {
    try {
      const response = await fetchWithRetry({
        method: "put",
        url: `/api/admin/coins/update-payment-gateway/${gatewayId}`,
        data: updatedData,
      });
      setNotificationMessage(response.data.message || "Cập nhật cổng thanh toán thành công!");
      loadPaymentGateways();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi cập nhật cổng thanh toán:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi cập nhật cổng thanh toán. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể cập nhật cổng thanh toán!");
      }
    }
  };

  // Xử lý xóa cổng thanh toán
  const handleDeletePaymentGateway = async (gatewayId) => {
    if (!window.confirm("Bạn có chắc muốn xóa cổng thanh toán này?")) return;
    try {
      const response = await fetchWithRetry({
        method: "delete",
        url: `/api/admin/coins/delete-payment-gateway/${gatewayId}`,
      });
      setNotificationMessage(response.data.message || "Xóa cổng thanh toán thành công!");
      loadPaymentGateways();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      console.error("Lỗi khi xóa cổng thanh toán:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi xóa cổng thanh toán. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.response?.data?.message || "Không thể xóa cổng thanh toán!");
      }
    }
  };

  // Lọc, sắp xếp cổng thanh toán
  useEffect(() => {
    let filtered = [...gateways];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (gateway) =>
          gateway.name.toLowerCase().includes(lowerSearchTerm) ||
          gateway.label.toLowerCase().includes(lowerSearchTerm) ||
          (gateway.description && gateway.description.toLowerCase().includes(lowerSearchTerm))
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((gateway) => gateway.enabled.toString() === filterStatus);
    }

    filtered.sort((a, b) => {
      const fieldA = a[sortField] || "";
      const fieldB = b[sortField] || "";
      if (sortField === "updatedAt" || sortField === "createdAt") {
        return sortOrder === "asc" ? new Date(fieldA) - new Date(fieldB) : new Date(fieldB) - new Date(fieldA);
      } else {
        return sortOrder === "asc"
          ? fieldA.toString().localeCompare(fieldB.toString())
          : fieldB.toString().localeCompare(fieldA.toString());
      }
    });

    setFilteredGateways(filtered);
  }, [gateways, searchTerm, filterStatus, sortField, sortOrder]);

  // Phân trang
  const totalItems = filteredGateways.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGateways = filteredGateways.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Kiểm tra quyền truy cập và tải dữ liệu
  useEffect(() => {
    if (isLoading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) {
      navigate("/login");
      return;
    }
    loadPaymentGateways();

    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "payment_gateway_updated") {
        setNotificationMessage(`Cổng thanh toán đã được cập nhật: ${notification.message}`);
        loadPaymentGateways();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [user, navigate, loadPaymentGateways, isLoading]);

  if (loading) return <p className="text-center text-gray-500">Đang tải...</p>;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Quản lý cổng thanh toán rút tiền</h2>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      {/* Form thêm cổng thanh toán */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Thêm cổng thanh toán mới</h3>
        <form onSubmit={handleAddPaymentGateway} className="p-6 bg-white rounded-xl shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Tên cổng (ID)</label>
              <input
                type="text"
                value={newGateway.name}
                onChange={(e) => setNewGateway({ ...newGateway, name: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập tên cổng (ví dụ: paypal)"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Tên hiển thị</label>
              <input
                type="text"
                value={newGateway.label}
                onChange={(e) => setNewGateway({ ...newGateway, label: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập tên hiển thị (ví dụ: Paypal)"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">Mô tả</label>
              <textarea
                value={newGateway.description}
                onChange={(e) => setNewGateway({ ...newGateway, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập mô tả (tùy chọn)"
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
          >
            Thêm cổng thanh toán
          </button>
        </form>
      </div>

      {/* Danh sách cổng thanh toán */}
      {filteredGateways.length > 0 && (
        <>
          <div className="mb-6 flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên cổng, tên hiển thị, mô tả..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Lọc theo trạng thái</option>
                <option value="true">Kích hoạt</option>
                <option value="false">Tắt</option>
              </select>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-blue-500 text-white">
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("name")}>
                    Tên cổng {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("label")}>
                    Tên hiển thị {sortField === "label" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("description")}>
                    Mô tả {sortField === "description" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("enabled")}>
                    Trạng thái {sortField === "enabled" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left cursor-pointer" onClick={() => handleSort("updatedAt")}>
                    Ngày cập nhật {sortField === "updatedAt" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="p-4 text-left">Hành động</th>
                </tr>
              </thead>
              <tbody>
                {currentGateways.map((gateway) => {
                  const { backgroundColor, textColor } = getStatusStyles(gateway.enabled);

                  return (
                    <tr key={gateway._id} className="border-b hover:bg-gray-50 transition duration-200">
                      <td className="p-4">{gateway.name}</td>
                      <td className="p-4">{gateway.label}</td>
                      <td className="p-4">{gateway.description || "N/A"}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleUpdatePaymentGateway(gateway._id, { enabled: !gateway.enabled })}
                          className={`inline-block px-3 py-1 rounded-full ${backgroundColor} ${textColor} hover:opacity-80`}
                        >
                          {gateway.enabled ? "Kích hoạt" : "Tắt"}
                        </button>
                      </td>
                      <td className="p-4">{new Date(gateway.updatedAt).toLocaleString("vi-VN")}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleDeletePaymentGateway(gateway._id)}
                          className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>

          <div className="mt-6 flex justify-between items-center">
            <div>
              <label className="mr-2">Hiển thị:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="p-2 border border-gray-300 rounded-lg"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="ml-2">mục/trang</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Trước
              </button>
              <span className="px-4 py-1">
                Trang {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-1 bg-blue-500 text-white rounded-lg disabled:bg-gray-300"
              >
                Sau
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminWithdrawalPaymentGateways;